import { createFileRoute, useRouter, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Gem } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/lib/useSubscription";
import { supabase } from "@/lib/supabase";
import { PHOTO_BUCKET, useResolvedPhotoUrl } from "@/components/ui-extras";
import { track } from "@/lib/analytics";
import { Sentry } from "@/lib/sentry";
import {
  analyzeWithCaileveEye, getAffiliateLink,
  CaileveEyeException,
  type CaileveEyeVerdict,
  analyzeAgainstAnchorLook,
  type CaileveEyeWithLookVerdict,
} from "@/lib/caileve-eye";
import { CaileveEyeIcon } from "@/components/caileve-eye-icon";
import { scanItemPhoto } from "@/lib/scan-item";
import { OccasionState } from "@/components/eye-occasion-state";
import { VerdictState } from "@/components/eye-verdict-state";
import { NotificationSoftAsk } from "@/components/notification-soft-ask";
import type { WeatherContext } from "@/lib/weather";

export const Route = createFileRoute("/caileve-eye")({
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s.token === 'string' ? s.token : undefined,
    mode: s.mode === "wardrobe" || s.mode === "look" ? s.mode : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Cailève Eye — Is this you?" },
      {
        name: "description",
        content:
          "Photograph any jewelry piece and Cailève will tell you if it belongs in your collection.",
      },
    ],
  }),
  component: EcrinEyeRoute,
});

type Phase =
  | "mode_select"
  | "occasion"
  | "camera"
  | "analyzing"
  | "verdict"
  | "limit"
  | "anchor_camera"
  | "anchor_captured"
  | "with_look_camera"
  | "with_look_analyzing"
  | "with_look_verdict";

type GiftProfile = {
  token: string;
  archetype_primary: string;
  archetype_secondary: string | null;
  suppressed_archetype: string | null;
  feelings_vocabulary: string[];
  metal_tone: string | null;
  color_register: string | null;
  occasion_profile: string | null;
  expires_at: string;
  owner_id: string;
};

function EcrinEyeRoute() {
  const { user } = useAuth();
  if (!user) return null;
  return <EcrinEye userId={user.id} />;
}

function EcrinEye({ userId }: { userId: string }) {
  const router = useRouter();
  const navigate = useNavigate();
  const sub = useSubscription();
  const { token: giftToken, mode: deepLinkMode } = Route.useSearch();
  const [giftProfile, setGiftProfile] = useState<GiftProfile | null>(null);

  useEffect(() => {
    if (!giftToken) return;
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gift-profile?token=${giftToken}`)
      .then(r => r.json())
      .then(data => {
        if (data.token) setGiftProfile(data);
        else setGiftProfile(null);
      })
      .catch(() => setGiftProfile(null));
  }, [giftToken]);

  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  const initialPhase: Phase =
    deepLinkMode === "wardrobe" ? "occasion" : deepLinkMode === "look" ? "anchor_camera" : "mode_select";
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [verdict, setVerdict] = useState<CaileveEyeVerdict | null>(null);
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const currentScanIdRef = useRef<string | null>(null);
  const [scansThisMonth, setScansThisMonth] = useState<number | null>(null);
  const [scannedPhotoPath, setScannedPhotoPath] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [giftMode, setGiftMode] = useState(false);
  const [occasionMode, setOccasionMode] = useState(false);
  const [occasionContext, setOccasionContext] = useState<{
    occasion: string;
    timeframe: string;
    note: string;
    weather?: WeatherContext | null;
  }>({ occasion: "", timeframe: "", note: "", weather: null });
  const [editedDescription, setEditedDescription] = useState("");
  const [editedCategory, setEditedCategory] = useState<
    "jewelry" | "clothing" | "accessory"
  >("jewelry");
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);
  const anchorInputRef = useRef<HTMLInputElement>(null);
  const anchorLibraryInputRef = useRef<HTMLInputElement>(null);
  const [anchorUrl, setAnchorUrl] = useState<string | null>(null);
  const [anchorFile, setAnchorFile] = useState<File | null>(null);
  const [candidateUrl, setCandidateUrl] = useState<string | null>(null);
  const [candidateFile, setCandidateFile] = useState<File | null>(null);
  const [anchorPhotoPath, setAnchorPhotoPath] = useState<string | null>(null);
  const [candidatePhotoPath, setCandidatePhotoPath] = useState<string | null>(null);
  const [withLookVerdict, setWithLookVerdict] =
    useState<CaileveEyeWithLookVerdict | null>(null);
  const phaseRef = useRef<Phase>(initialPhase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const { data: totalCatalogSize } = useQuery({
    queryKey: ["total_count_eye", userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [{ count: j }, { count: c }] = await Promise.all([
        supabase.from("jewelry_items").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("clothing_items").select("id", { count: "exact", head: true }).eq("user_id", userId),
      ]);
      return (j ?? 0) + (c ?? 0);
    },
  });

  // Page open analytics
  useEffect(() => {
    track("ecrin_eye_opened");
  }, []);

  // Free-tier scan counter
  useEffect(() => {
    let cancelled = false;
    if (!sub.isFree) {
      setScansThisMonth(null);
      return;
    }
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    // ecrin_eye_scans is the production table name — do not rename
    supabase
      .from("ecrin_eye_scans")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .gte("scanned_at", start.toISOString())
      .then(({ count }) => {
        if (!cancelled) setScansThisMonth(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, sub.isFree, phase]);

  // Clean up captured object URLs
  useEffect(() => {
    return () => {
      if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    };
  }, [capturedUrl]);

  const handleFile = async (file: File) => {
    // With-look flow: route to anchor-vs-candidate analysis
    if (phaseRef.current === "with_look_camera") {
      if (candidateUrl) URL.revokeObjectURL(candidateUrl);
      const url = URL.createObjectURL(file);
      setCandidateUrl(url);
      setCandidateFile(file);
      setPhase("with_look_analyzing");
      if (!anchorFile) {
        toast.error("Anchor look missing. Please retake.");
        setPhase("anchor_camera");
        return;
      }
      try {
        const result = await analyzeAgainstAnchorLook(
          anchorFile,
          file,
          userId,
          occasionMode ? occasionContext : undefined,
        );
        setWithLookVerdict(result);
        setPhase("with_look_verdict");
        if (result.scan_id && anchorFile) {
          try {
            const anchorPath = `${userId}/scanned/anchor_${crypto.randomUUID()}.jpg`;
            const candidatePath = `${userId}/scanned/${crypto.randomUUID()}.jpg`;
            await Promise.all([
              supabase.storage.from(PHOTO_BUCKET).upload(anchorPath, anchorFile, { cacheControl: "3600", upsert: false, contentType: "image/jpeg" }),
              supabase.storage.from(PHOTO_BUCKET).upload(candidatePath, file, { cacheControl: "3600", upsert: false, contentType: "image/jpeg" }),
            ]);
            await supabase
              .from("ecrin_eye_scans")
              .update({ anchor_photo_path: anchorPath, scanned_photo_path: candidatePath } as never)
              .eq("id", result.scan_id);
            setAnchorPhotoPath(anchorPath);
            setCandidatePhotoPath(candidatePath);
          } catch {
            // Non-critical — Save Pairing / Add to Considering will no-op without paths
          }
        }
      } catch (err) {
        if (
          err instanceof CaileveEyeException &&
          err.code === "scan_limit_reached"
        ) {
          setPhase("limit");
          return;
        }
        if (
          err instanceof CaileveEyeException &&
          err.code === "network_error"
        ) {
          toast.error("Connection issue. Check your signal and try again.");
          setPhase("anchor_captured");
          return;
        }
        toast.error("Unable to scan right now. Please try again.");
        setPhase("anchor_captured");
      }
      return;
    }

    if (capturedUrl) URL.revokeObjectURL(capturedUrl);
    const url = URL.createObjectURL(file);
    setCapturedUrl(url);
    setCapturedFile(file);
    setPhase("analyzing");
    track("ecrin_eye_photo_taken");
    setEditedDescription("");
    setEditedCategory("jewelry");
    // Do not reset giftMode here — user may intentionally
    // keep gift mode on for multiple scans in one session.
    // giftMode resets in onScanAnother only.
    try {
      const v = await analyzeWithCaileveEye(
        file,
        userId,
        giftMode,
        occasionMode
          ? occasionContext
          : occasionContext.weather
          ? { occasion: "", timeframe: "", note: "", weather: occasionContext.weather }
          : undefined,
        giftProfile ?? null,
      );
      setVerdict(v);
      setCurrentScanId(v.scan_id ?? null);
      currentScanIdRef.current = v.scan_id ?? null;
      setEditedDescription(v.item_description ?? "");
      setEditedCategory(v.item_category ?? "jewelry");
      setPhase("verdict");
      // Upload photo immediately so path is available when user taps
      // See It In My Wardrobe. sessionStorage is NOT written here —
      // only onSeePairs may write caileve_scanned_piece.
      if (file) {
        setUploadingPhoto(true);
        try {
          const uploadPath = `${userId}/scanned/${crypto.randomUUID()}.jpg`;
          const { error: upErr } = await supabase.storage
            .from(PHOTO_BUCKET)
            .upload(uploadPath, file, {
              cacheControl: "3600",
              upsert: false,
              contentType: "image/jpeg",
            });
          if (!upErr) {
            setScannedPhotoPath(uploadPath);
            // Persist photo path to scan row for cross-trip intelligence
            if (currentScanIdRef.current) {
              supabase
                .from("ecrin_eye_scans")
                .update({ scanned_photo_path: uploadPath } as never)
                .eq("id", currentScanIdRef.current)
                .then(() => {/* non-critical — fire and forget */});
            }
          }
        } catch {
          // Non-critical — flow continues without photo
        } finally {
          setUploadingPhoto(false);
        }
      }
    } catch (err) {
      if (
        err instanceof CaileveEyeException &&
        err.code === "scan_limit_reached"
      ) {
        setPhase("limit");
        return;
      }
      if (
        err instanceof CaileveEyeException &&
        err.code === "network_error"
      ) {
        toast.error("Connection issue. Check your signal and try again.");
        setPhase("camera");
        return;
      }
      toast.error("Unable to scan right now. Please try again.");
      setPhase("camera");
    }
  };

  const onFileChange =
    (ref: React.RefObject<HTMLInputElement | null>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (ref.current) ref.current.value = "";
      if (file) handleFile(file);
    };

  const onSavePairing = async () => {
    if (!withLookVerdict || !userId) return;
    const lookName = occasionContext.occasion
      ? `${occasionContext.occasion} — ${withLookVerdict.item_description || "piece"} with your look`
      : `${withLookVerdict.item_description || "Piece"} with your look`;
    const notes = [
      withLookVerdict.verdict_copy,
      withLookVerdict.styling_guidance ? `Style it this way: ${withLookVerdict.styling_guidance}` : null,
      withLookVerdict.avoid_text ? `Avoid: ${withLookVerdict.avoid_text}` : null,
      withLookVerdict.one_last_thing,
    ].filter(Boolean).join("\n\n");
    await supabase.from("saved_looks").insert({
      user_id: userId,
      name: lookName,
      occasion: occasionContext.occasion || null,
      rating: 5,
      styling_notes: notes.slice(0, 2000),
      look_photo_url: anchorPhotoPath ?? null,
    } as never);
    toast.success("Saved to your Lookbook.");
  };

  const onAddToConsidering = async () => {
    if (!withLookVerdict || !userId || !candidatePhotoPath) return;
    const table = withLookVerdict.item_category === "clothing" ? "clothing_items" : "jewelry_items";
    await supabase.from(table).insert({
      user_id: userId,
      name: withLookVerdict.item_description || "Scanned piece",
      photo_url: candidatePhotoPath,
      is_wishlist: true,
    } as never);
    toast.success("Added to Your Edit.");
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch" as const,
      backgroundColor: "var(--caileve-bg)",
    }}>
      <style>{`
        @keyframes caileve-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.08); }
        }
      `}</style>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileChange(cameraInputRef)}
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileChange(libraryInputRef)}
      />
      <input
        ref={anchorInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (anchorInputRef.current) anchorInputRef.current.value = "";
          if (file) {
            if (anchorUrl) URL.revokeObjectURL(anchorUrl);
            setAnchorUrl(URL.createObjectURL(file));
            setAnchorFile(file);
            setPhase("anchor_captured");
          }
        }}
      />
      <input
        ref={anchorLibraryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (anchorLibraryInputRef.current) anchorLibraryInputRef.current.value = "";
          if (file) {
            if (anchorUrl) URL.revokeObjectURL(anchorUrl);
            setAnchorUrl(URL.createObjectURL(file));
            setAnchorFile(file);
            setPhase("anchor_captured");
          }
        }}
      />

      {phase === "mode_select" && (
        <ModeSelectState
          onBack={() => router.history.back()}
          onSelectWardrobe={() => setPhase("occasion")}
          onSelectLook={() => setPhase("anchor_camera")}
          onShoppingForSomeoneElse={() => {
            setGiftMode(true);
            setPhase("occasion");
          }}
        />
      )}

      {phase === "occasion" && (
        <OccasionState
          onBack={() => (deepLinkMode ? router.history.back() : setPhase("mode_select"))}
          context={occasionContext}
          onContextChange={setOccasionContext}
          onJustBrowsing={() => {
            setOccasionMode(false);
            setOccasionContext((c) => ({
              occasion: "",
              timeframe: "",
              note: "",
              // keep any prefilled weather so the camera path can still use it
              weather: c.weather ?? null,
            }));
            setPhase("camera");
          }}
          onStart={() => {
            setOccasionMode(true);
            setPhase("camera");
          }}
          giftProfile={giftProfile}
        />
      )}

      {phase === "camera" && (
        <CameraState
          tier={sub.tier}
          scansThisMonth={scansThisMonth}
          onBack={() => setPhase("occasion")}
          onTakePhoto={() => cameraInputRef.current?.click()}
          onChooseFromLibrary={() => libraryInputRef.current?.click()}
          isMobile={isMobile}
        />
      )}

      {phase === "analyzing" && capturedUrl && (
        <AnalyzingState imageUrl={capturedUrl} />
      )}

      {phase === "verdict" && verdict && capturedUrl && (
        <VerdictState
          userId={userId}
          scanId={currentScanId}
          imageUrl={capturedUrl}
          capturedFile={capturedFile}
          scannedPhotoPath={scannedPhotoPath}
          uploadingPhoto={uploadingPhoto}
          verdict={verdict}
          editedDescription={editedDescription}
          editedCategory={editedCategory}
          weather={occasionContext.weather}
          onEditedDescriptionChange={setEditedDescription}
          onEditedCategoryChange={setEditedCategory}
          catalogSize={totalCatalogSize ?? null}
          tier={sub.tier ?? null}
          occasionUsed={occasionContext.occasion || null}
          giftProfile={giftProfile}
          onScanAnother={() => {
            if (capturedUrl) URL.revokeObjectURL(capturedUrl);
            setCapturedUrl(null);
            setCapturedFile(null);
            setVerdict(null);
            setCurrentScanId(null);
            currentScanIdRef.current = null;
            setScannedPhotoPath(null);
            setUploadingPhoto(false);
            setGiftMode(false);
            setEditedDescription("");
            setEditedCategory("jewelry");
            setOccasionMode(false);
            setOccasionContext((c) => ({
              occasion: "",
              timeframe: "",
              note: "",
              // keep any prefilled weather, same as the Just Browsing
              // path already does, so the next scan still has it
              weather: c.weather ?? null,
            }));
            try {
              sessionStorage.removeItem("caileve_scanned_piece");
            } catch {}
            setPhase("camera");
          }}
          onSeePairs={() => {
            track("ecrin_eye_look_generated");
            try {
              sessionStorage.setItem(
                "caileve_scanned_piece",
                JSON.stringify({
                  description: verdict.item_description ?? "",
                  category: verdict.item_category ?? "jewelry",
                  verdict_type: verdict.verdict_type ?? "",
                  photoPath: scannedPhotoPath,
                  occasion: occasionMode ? occasionContext.occasion : null,
                  timeframe: occasionMode ? occasionContext.timeframe : null,
                })
              );
            } catch {
              // Non-critical
            }

            navigate({ to: "/suggest" });
          }}
        />
      )}

      {phase === "verdict" && <NotificationSoftAsk />}

      {phase === "anchor_camera" && (
        <CameraState
          tier={sub.tier}
          scansThisMonth={scansThisMonth}
          onBack={() => (deepLinkMode ? router.history.back() : setPhase("mode_select"))}
          onTakePhoto={() => anchorInputRef.current?.click()}
          onChooseFromLibrary={() => anchorLibraryInputRef.current?.click()}
          isMobile={isMobile}
          instructionText="Take a photo of the anchor piece"
        />
      )}

      {phase === "anchor_captured" && anchorUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "var(--caileve-bg)",
            paddingTop: "calc(env(safe-area-inset-top) + 16px)",
            paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
            paddingLeft: 24,
            paddingRight: 24,
          }}
        >
          <div className="flex items-start justify-between">
            <button
              type="button"
              onClick={() => setPhase("anchor_camera")}
              aria-label="Retake anchor"
              className="p-2 -ml-2"
              style={{ color: "var(--caileve-ink)" }}
            >
              <ArrowLeft size={24} />
            </button>
            <span
              className="uppercase"
              style={{
                fontSize: 10,
                color: "var(--caileve-muted)",
                letterSpacing: "0.08em",
                marginTop: 10,
              }}
            >
              ANCHOR LOOK
            </span>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <img
              src={anchorUrl}
              alt="Anchor look"
              style={{
                maxWidth: "100%",
                maxHeight: "55vh",
                objectFit: "contain",
                borderRadius: 4,
              }}
            />
            <p
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 15,
                color: "var(--caileve-muted)",
                textAlign: "center",
                margin: 0,
              }}
            >
              Cailève will check whether the piece belongs with this look.
            </p>
          </div>
          <div className="flex flex-col gap-3 pb-24">
            <button
              type="button"
              onClick={() => setPhase("with_look_camera")}
              className="w-full py-3 text-sm uppercase tracking-[0.2em]"
              style={{
                backgroundColor: "var(--caileve-lilac)",
                color: "var(--caileve-bg)",
                border: "1px solid rgba(198,166,107,0.35)",
                borderRadius: 4,
              }}
            >
              Scan the Piece
            </button>
            <button
              type="button"
              onClick={() => setPhase("anchor_camera")}
              style={{
                width: "100%",
                padding: "12px 0",
                background: "transparent",
                border: "none",
                fontSize: 12,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "var(--caileve-muted)",
                cursor: "pointer",
              }}
            >
              Retake anchor look
            </button>
          </div>
        </div>
      )}

      {phase === "with_look_camera" && (
        <CameraState
          tier={sub.tier}
          scansThisMonth={scansThisMonth}
          onBack={() => setPhase("anchor_captured")}
          onTakePhoto={() => cameraInputRef.current?.click()}
          onChooseFromLibrary={() => libraryInputRef.current?.click()}
          isMobile={isMobile}
        />
      )}

      {phase === "with_look_analyzing" && candidateUrl && (
        <AnalyzingState imageUrl={candidateUrl} />
      )}

      {phase === "with_look_verdict" && withLookVerdict && candidateUrl && (
        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "var(--caileve-bg)",
            paddingTop: "calc(env(safe-area-inset-top) + 24px)",
            paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
            paddingLeft: 24,
            paddingRight: 24,
          }}
        >
          <img
            src={candidateUrl}
            alt="Candidate piece"
            style={{
              width: "100%",
              maxHeight: "40vh",
              objectFit: "contain",
              borderRadius: 4,
              marginBottom: 24,
            }}
          />
          <h2
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 26,
              color: "var(--caileve-ink)",
              margin: 0,
            }}
          >
            {withLookVerdict.with_look_verdict === "it_belongs"
              ? "It belongs."
              : withLookVerdict.with_look_verdict === "works_with_restraint"
              ? "It works, with restraint."
              : withLookVerdict.with_look_verdict === "not_the_strongest_choice"
              ? "Not the strongest choice."
              : withLookVerdict.with_look_verdict === "save_for_another_look"
              ? "Save it for another look."
              : "I need a clearer view."}
          </h2>
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              fontSize: 16,
              color: "var(--caileve-ink)",
              lineHeight: 1.55,
              marginTop: 16,
            }}
          >
            {withLookVerdict.verdict_copy}
          </p>
          {withLookVerdict.styling_guidance && (
            <p
              style={{
                fontSize: 14,
                color: "var(--caileve-muted)",
                marginTop: 16,
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "var(--caileve-ink)" }}>Style it this way:</strong>{" "}
              {withLookVerdict.styling_guidance}
            </p>
          )}
          {withLookVerdict.avoid_text && (
            <p
              style={{
                fontSize: 14,
                color: "var(--caileve-muted)",
                marginTop: 12,
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "var(--caileve-ink)" }}>Avoid:</strong>{" "}
              {withLookVerdict.avoid_text}
            </p>
          )}
          {withLookVerdict.one_last_thing && (
            <p
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 14,
                color: "var(--caileve-muted)",
                marginTop: 16,
                lineHeight: 1.6,
              }}
            >
              {withLookVerdict.one_last_thing}
            </p>
          )}
          <button
            type="button"
            onClick={onSavePairing}
            className="w-full py-3 text-sm uppercase tracking-[0.2em]"
            style={{ marginTop: 24, backgroundColor: "var(--caileve-gold)", color: "var(--caileve-bg)", border: "none", borderRadius: "var(--caileve-radius-pill)", marginBottom: 12 }}
          >
            Save Pairing
          </button>
          <button
            type="button"
            onClick={onAddToConsidering}
            className="w-full py-3 text-sm uppercase tracking-[0.2em]"
            style={{ backgroundColor: "transparent", color: "var(--caileve-lilac)", border: "1px solid var(--caileve-lilac)", borderRadius: "var(--caileve-radius-pill)", marginBottom: 12 }}
          >
            Add Item to Considering
          </button>
          <button
            type="button"
            onClick={() => {
              if (candidateUrl) URL.revokeObjectURL(candidateUrl);
              setCandidateUrl(null);
              setCandidateFile(null);
              setWithLookVerdict(null);
              setPhase("with_look_camera");
            }}
            className="w-full py-3 text-sm uppercase tracking-[0.2em]"
            style={{
              backgroundColor: "var(--caileve-lilac)",
              color: "var(--caileve-bg)",
              border: "1px solid rgba(198,166,107,0.35)",
              borderRadius: 4,
            }}
          >
            Try Another Piece With This Look
          </button>
          <button
            type="button"
            onClick={() => {
              if (anchorUrl) URL.revokeObjectURL(anchorUrl);
              if (candidateUrl) URL.revokeObjectURL(candidateUrl);
              setAnchorUrl(null);
              setAnchorFile(null);
              setCandidateUrl(null);
              setCandidateFile(null);
              setWithLookVerdict(null);
              setPhase("anchor_camera");
            }}
            style={{
              width: "100%",
              marginTop: 12,
              padding: "12px 0",
              background: "transparent",
              border: "none",
              fontSize: 12,
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: "italic",
              color: "var(--caileve-muted)",
              cursor: "pointer",
            }}
          >
            Change Anchor Look
          </button>
        </div>
      )}

      {phase === "limit" && (
        <LimitState
          onUpgrade={() => navigate({ to: "/plans", search: { success: false, tier: undefined } })}
          onLater={() => navigate({ to: "/" })}
        />
      )}
    </div>
  );
}

// OccasionState extracted to src/components/eye-occasion-state.tsx


function CameraState({
  tier,
  scansThisMonth,
  onBack,
  onTakePhoto,
  onChooseFromLibrary,
  isMobile,
  instructionText,
}: {
  tier: string;
  scansThisMonth: number | null;
  onBack: () => void;
  onTakePhoto: () => void;
  onChooseFromLibrary: () => void;
  isMobile: boolean;
  instructionText?: string;
}) {
  const remaining =
    tier === "free" && scansThisMonth != null
      ? Math.max(0, 3 - scansThisMonth)
      : null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--caileve-bg)",
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      <div className="flex items-start justify-between">
        <button
          type="button"
          onClick={onBack}
          aria-label="Go back"
          className="p-2 -ml-2"
          style={{ color: "var(--caileve-ink)" }}
        >
          <ArrowLeft size={24} />
        </button>
        <span
          className="uppercase"
          style={{
            fontSize: 10,
            color: "var(--caileve-muted)",
            letterSpacing: "0.08em",
            marginTop: 10,
          }}
        >
          EARLY ACCESS
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
        <CaileveEyeIcon size={48} color="var(--caileve-gold)" />
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 32,
            color: "var(--caileve-ink)",
            marginTop: 16,
          }}
        >
          Cailève Eye
        </h1>
        <p
          style={{
            fontSize: 14,
            fontStyle: "italic",
            color: "var(--caileve-muted)",
            marginTop: 8,
          }}
        >
          {instructionText ?? "Photograph any piece to see if it's you"}
        </p>
        {remaining != null && (
          <p style={{ fontSize: 12, color: "var(--caileve-muted)", marginTop: 12 }}>
            {remaining} of 3 scans remaining this month
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 pb-24">
        <button
          type="button"
          onClick={onTakePhoto}
          aria-label="Take photo"
          className="w-full py-3 text-sm uppercase tracking-[0.2em]"
          style={{ backgroundColor: "var(--caileve-gold)", color: "var(--caileve-bg)", borderRadius: "var(--caileve-radius-pill)" }}
        >
          Take photo
        </button>
        <button
          type="button"
          onClick={onChooseFromLibrary}
          aria-label="Choose from library"
          className="w-full py-3 text-sm uppercase tracking-[0.2em]"
          style={{
            border: "1px solid var(--caileve-lilac)",
            color: "var(--caileve-lilac)",
            backgroundColor: "transparent",
          }}
        >
          Choose from library
        </button>
      </div>
    </div>
  );
}

function AnalyzingState({ imageUrl }: { imageUrl: string }) {
  const ANALYZING_MESSAGES = [
    "Reading your collection…",
    "Weighing what you already own…",
    "Looking for what this would sit beside…",
    "Almost there…",
  ];

  const [msgIndex, setMsgIndex] = useState(0);
  const [msgVisible, setMsgVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgVisible(false);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % ANALYZING_MESSAGES.length);
        setMsgVisible(true);
      }, 200);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
      <img
        src={imageUrl}
        alt="Analyzing"
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "var(--caileve-ink)",
          opacity: 0.4,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 24,
        }}
      >
        <p
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
            fontSize: 20,
            color: "var(--caileve-bg)",
            textAlign: "center",
            padding: "0 24px",
            opacity: msgVisible ? 1 : 0,
            transition: "opacity 200ms ease",
          }}
        >
          {ANALYZING_MESSAGES[msgIndex]}
        </p>
        <div
          style={{
            width: 60,
            height: 60,
            border: "2px solid var(--caileve-gold)",
            borderRadius: "50%",
            animation: "caileve-pulse 1.8s ease-in-out infinite",
          }}
        />
      </div>
    </div>
  );
}


function LimitState({
  onUpgrade,
  onLater,
}: {
  onUpgrade: () => void;
  onLater: () => void;
}) {
  useEffect(() => {
    track("ecrin_eye_scan_limit_hit");
    track("upgrade_prompted");
  }, []);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "var(--caileve-bg)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 1,
          backgroundColor: "var(--caileve-gold)",
          marginBottom: 24,
        }}
      />
      <h1
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 22,
          color: "var(--caileve-ink)",
          textAlign: "center",
          margin: 0,
        }}
      >
        You've used your 3 free scans this month.
      </h1>
      <p
        style={{
          fontSize: 15,
          color: "var(--caileve-muted)",
          textAlign: "center",
          marginTop: 16,
          maxWidth: 360,
        }}
      >
        Upgrade to scan without limits — and let Cailève read every piece you
        consider.
      </p>
      <button
        type="button"
        onClick={onUpgrade}
        className="py-3 px-8 text-sm uppercase tracking-[0.2em] mt-8 w-full max-w-[320px]"
        style={{ backgroundColor: "var(--caileve-gold)", color: "var(--caileve-bg)", borderRadius: "var(--caileve-radius-pill)" }}
      >
        Upgrade to Premium
      </button>
      <button
        type="button"
        onClick={onLater}
        className="py-3 px-8 text-sm uppercase tracking-[0.2em] mt-3 w-full max-w-[320px]"
        style={{
          border: "1px solid var(--caileve-lilac)",
          color: "var(--caileve-lilac)",
          backgroundColor: "transparent",
        }}
      >
        Remind me next month
      </button>
    </div>
  );
}

function ModeSelectState({
  onBack,
  onSelectWardrobe,
  onSelectLook,
  onShoppingForSomeoneElse,
}: {
  onBack: () => void;
  onSelectWardrobe: () => void;
  onSelectLook: () => void;
  onShoppingForSomeoneElse: () => void;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        overflowY: "auto",
        backgroundColor: "var(--caileve-bg)",
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 100px)",
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Go back"
        className="p-2 -ml-2"
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--caileve-ink)" }}
      >
        <ArrowLeft size={24} />
      </button>

      <h1
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 26,
          color: "var(--caileve-ink)",
          marginTop: 24,
          lineHeight: 1.2,
        }}
      >
        What are you deciding?
      </h1>
      <p
        style={{
          fontSize: 14,
          fontStyle: "italic",
          color: "var(--caileve-muted)",
          marginTop: 8,
        }}
      >
        Cailève Eye reads the piece, the moment, and the look.
      </p>

      <button
        type="button"
        onClick={onSelectWardrobe}
        style={{
          width: "100%",
          textAlign: "center",
          marginTop: 28,
          padding: "32px 20px",
          borderRadius: "var(--caileve-radius-lg)",
          border: "1px solid var(--caileve-soft)",
          background: "linear-gradient(145deg, rgba(155,127,168,0.08), rgba(237,228,240,0.55))",
          cursor: "pointer",
        }}
      >
        <Gem size={32} color="var(--caileve-muted)" strokeWidth={1.5} />
        <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, color: "var(--caileve-ink)", marginTop: 12 }}>
          Wardrobe Fit
        </p>
        <p style={{ fontSize: 14, color: "var(--caileve-muted)", marginTop: 6 }}>
          See whether this piece belongs in your collection.
        </p>
        <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em", color: "var(--caileve-lilac)", marginTop: 16 }}>
          Scan a Piece
        </p>
      </button>

      <button
        type="button"
        onClick={onSelectLook}
        style={{
          width: "100%",
          textAlign: "center",
          marginTop: 16,
          padding: "32px 20px",
          borderRadius: "var(--caileve-radius-lg)",
          border: "1px solid rgba(198,166,107,0.45)",
          background: "linear-gradient(145deg, rgba(255,255,255,0.62), rgba(237,228,240,0.74))",
          cursor: "pointer",
          position: "relative",
        }}
      >
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--caileve-gold)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v3" />
          <path d="M9 5h6" />
          <path d="M9 5l-3 3v2l2-1 1 3-1 9h8l-1-9 1-3 2 1v-2l-3-3" />
        </svg>
          <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, color: "var(--caileve-ink)", marginTop: 12 }}>
            Final Touch
          </p>
        <p style={{ fontSize: 14, color: "var(--caileve-muted)", marginTop: 6 }}>
          Anchor what you're wearing, then check the final piece.
        </p>
        <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.15em", color: "var(--caileve-gold-text)", marginTop: 16 }}>
          Start With the Look
        </p>
      </button>

      <button
        type="button"
        onClick={onShoppingForSomeoneElse}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 20,
          fontSize: 13,
          fontStyle: "italic",
          color: "var(--caileve-muted)",
        }}
      >
        Shopping for someone else?
      </button>
    </div>
  );
}